import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { profileDescription } from './profileDescriptions';
import './segmentHoverCard.css';

/**
 * The segment a hover card describes. `description` ships today; `tags` and `quote` are
 * ready slots wired in once the research copy lands (undefined -> those sections don't render).
 */
export interface HoverSegment {
  name: string;
  twinCount: number;
  description: string;
  tags?: string[];
  quote?: string;
}

const HIDE_DELAY_MS = 80;
const DESCRIPTION_FALLBACK = 'Segment description coming soon.';

/**
 * Hover state for a segment list: `show(categoryName, archetype)` on row enter, `hide()` on
 * leave. The hide is delayed so moving between two adjacent rows (leave-then-enter) cancels it
 * and the card swaps smoothly instead of flickering; a scroll dismisses it. Pair the returned
 * `hoverSeg` with a single <SegmentHoverCard segment={hoverSeg} />.
 */
export function useSegmentHover() {
  const [hoverSeg, setHoverSeg] = useState<HoverSegment | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((categoryName: string, arch: { name: string; twin_count: number }) => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setHoverSeg({
      name: arch.name,
      twinCount: arch.twin_count,
      description: profileDescription(categoryName, arch.name) ?? DESCRIPTION_FALLBACK,
    });
  }, []);

  const hide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setHoverSeg(null), HIDE_DELAY_MS);
  }, []);

  useEffect(() => {
    if (!hoverSeg) return;
    const dismiss = () => setHoverSeg(null);
    window.addEventListener('scroll', dismiss, true);
    return () => window.removeEventListener('scroll', dismiss, true);
  }, [hoverSeg]);

  return { hoverSeg, show, hide };
}

// Offset from the cursor, and how long the cross-fade / progress sweep runs on a segment change.
const CURSOR_GAP = 18;
const EDGE_PAD = 8;
const SWAP_MS = 140;

/**
 * A floating card that previews an audience segment, positioned near the cursor and flipped to
 * stay on-screen. Rendered once (via a portal) and driven by the `segment` prop — null hides it.
 * Switching segments doesn't swap the text silently: a thin progress bar sweeps and the body
 * cross-fades, so the update reads as deliberate.
 */
const SegmentHoverCard: React.FC<{ segment: HoverSegment | null }> = ({ segment }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const lastPos = useRef({ x: 0, y: 0 });
  const activeRef = useRef(false);
  const swapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [displayed, setDisplayed] = useState<HoverSegment | null>(null);

  const place = (x: number, y: number) => {
    const el = cardRef.current;
    if (!el) return;
    const { offsetWidth: w, offsetHeight: h } = el;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = x + CURSOR_GAP;
    if (left + w > vw - EDGE_PAD) left = x - CURSOR_GAP - w; // flip left near the right edge
    if (left < EDGE_PAD) left = EDGE_PAD;
    let top = y + CURSOR_GAP;
    if (top + h > vh - EDGE_PAD) top = y - CURSOR_GAP - h; // flip above near the bottom edge
    if (top < EDGE_PAD) top = EDGE_PAD;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  };

  // One always-on listener: cheap position cache; only reads layout + repositions while visible.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      lastPos.current = { x: e.clientX, y: e.clientY };
      if (activeRef.current) place(e.clientX, e.clientY);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  // Visibility (is-on) is toggled imperatively so `className` can stay a constant string — that
  // keeps React from ever re-applying it and clobbering the classes/position we set on the ref.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    el.classList.toggle('is-on', !!segment);
    el.setAttribute('aria-hidden', segment ? 'false' : 'true');
  }, [segment]);

  useEffect(() => {
    activeRef.current = !!segment;
  }, [segment]);

  // Content transition: instant on first open, sweep + cross-fade when switching segments. The
  // is-swapping class is toggled imperatively (see above) and setDisplayed runs only in the
  // timeout callback, so nothing writes state synchronously during the effect.
  useEffect(() => {
    if (!segment) return; // hiding keeps the last content mounted so it fades out cleanly
    const el = cardRef.current;
    const instant = !displayed || displayed.name === segment.name;
    if (!instant) el?.classList.add('is-swapping');
    swapTimer.current = setTimeout(
      () => {
        setDisplayed(segment);
        el?.classList.remove('is-swapping');
      },
      instant ? 0 : SWAP_MS,
    );
    return () => {
      if (swapTimer.current) clearTimeout(swapTimer.current);
    };
  }, [segment, displayed]);

  // Re-anchor to the cursor once the card has measurable dimensions (first show / height change).
  useLayoutEffect(() => {
    if (segment) place(lastPos.current.x, lastPos.current.y);
  });

  const seg = displayed;
  return createPortal(
    <div ref={cardRef} className="mkt-hovercard" role="tooltip">
      <span className="mkt-hovercard__bar" aria-hidden="true" />
      <div className="mkt-hovercard__body">
        {seg && (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="text-[17px] font-semibold leading-tight text-[#111827] [text-wrap:balance]">
                {seg.name}
              </div>
              <span className="flex-none whitespace-nowrap rounded-full bg-[#E8FCFA] px-[11px] py-1 text-[12px] font-semibold tabular-nums text-[#0B7C74]">
                {seg.twinCount} twins
              </span>
            </div>

            {seg.tags && seg.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {seg.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-black/10 bg-white px-[11px] py-[5px] text-[12px] font-medium text-[#374151]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <p className="mt-[15px] text-[14px] leading-[1.55] text-[#374151]">{seg.description}</p>

            {seg.quote && (
              <div className="mt-[17px] border-l-2 border-[#07E5D1] pl-[15px]">
                <div className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[#9ca3af]">
                  In their words
                </div>
                <p className="mt-[7px] text-[13.5px] italic leading-[1.5] text-[#6b7280]">
                  “{seg.quote}”
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default SegmentHoverCard;
